import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { AuthLayout, AuthLink } from "@/components/AuthLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { formatApiError } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { useTheme } from "@/components/ThemeProvider";
import { stashPendingSelection } from "@/pages/SelectTenantPage";

export default function LoginPage() {
  const { login } = useAuth();
  const { theme } = useTheme();
  const brand = theme?.product_display_name || "Quanta";
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/app/dashboard";

  // Phase 12.4 — theme drawer preview strip.
  const previewMode = new URLSearchParams(location.search).get("_theme_preview") === "1";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [needsMfa, setNeedsMfa] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      // Phase 22 — no tenant_slug on the payload. Multi-tenant identities
      // land on /app/select-tenant with the temp token stashed.
      const res = await login({
        email: email.trim(),
        password,
        mfa_code: needsMfa ? mfaCode.trim() : undefined,
      });
      if (res.mfa_required) {
        setNeedsMfa(true);
      } else if (res.needs_tenant_selection) {
        stashPendingSelection(res);
        navigate("/app/select-tenant", { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Welcome back"
      subtitle={`Sign in to your ${brand} workspace.`}
      footer={
        <>
          Don't have a workspace yet? <AuthLink to="/signup" data-testid="link-signup">Create one</AuthLink>.
        </>
      }
    >
      {previewMode && (
        <div
          className="mb-4 rounded-md border border-secondary/30 bg-secondary/5 px-3 py-2 text-xs text-secondary"
          data-testid="theme-preview-strip"
        >
          You are previewing <span className="font-semibold">{brand}</span>.
          Sign-in is disabled in this window.
        </div>
      )}
      <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email">Work email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            data-testid="login-email"
          />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <AuthLink to="/forgot" data-testid="link-forgot">Forgot?</AuthLink>
          </div>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password"
          />
        </div>
        {needsMfa && (
          <div className="space-y-2">
            <Label htmlFor="mfa">Authenticator code</Label>
            <Input
              id="mfa"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
              placeholder="123456"
              autoFocus
              data-testid="login-mfa"
            />
            <p className="text-xs text-muted-foreground">Open your authenticator app and enter the 6-digit code.</p>
          </div>
        )}
        {error && (
          <Alert variant="destructive" data-testid="login-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <Button
          type="submit"
          className="w-full text-white hover:opacity-90"
          disabled={busy || previewMode}
          style={{ backgroundColor: "var(--quanta-blue)" }}
          data-testid="login-submit"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {needsMfa ? "Verify & sign in" : `Sign in to ${brand}`}
        </Button>
      </form>
    </AuthLayout>
  );
}
